import type { ReactNode } from "react";
import { MEAL_DETAIL_MACRO_CLASS } from "../../utils/nutritionMacroColors";

interface NutritionTotals {
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

interface FoodItem {
  id: number;
  weight: number;
}

interface NutritionFactsCardProps {
  nutritionTotals: NutritionTotals | null;
  foodItems: FoodItem[];
  /** Overrides the default "N items" header label (e.g. per-serving text on barcode flow). */
  headerMeta?: string;
  /** Extra rows rendered below the main grid (e.g. sodium, cholesterol). */
  bottomSlot?: ReactNode;
}

const NutritionFactsCard: React.FC<NutritionFactsCardProps> = ({
  nutritionTotals,
  foodItems,
  headerMeta,
  bottomSlot,
}) => {
  if (!nutritionTotals) {
    return null;
  }

  const fmtInt = (n: number) => Math.round(n).toString();
  const fmt1 = (n: number) => Math.round(n).toString();

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-md ring-1 ring-black/[0.03] dark:ring-white/[0.04]">
      <div className="mb-4 flex items-baseline justify-between gap-2">
        <h2 className="text-headline font-bold text-foreground">Total Nutrition</h2>
        <span className="text-footnote font-medium text-muted-foreground">
          {headerMeta ??
            `${foodItems.length} ${foodItems.length === 1 ? "item" : "items"}`}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
        <div>
          <p className="text-subhead text-muted-foreground mb-1">Calories</p>
          <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.calories}`}>
            {fmtInt(nutritionTotals.calorie_content_kcal)}
          </p>
        </div>
        <div>
          <p className="text-subhead text-muted-foreground mb-1">Protein</p>
          <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.protein}`}>
            {fmt1(nutritionTotals.protein_content_g)}g
          </p>
        </div>
        <div>
          <p className="text-subhead text-muted-foreground mb-1">Carbs</p>
          <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.carbs}`}>
            {fmt1(nutritionTotals.carbohydrate_content_g)}g
          </p>
        </div>
        <div>
          <p className="text-subhead text-muted-foreground mb-1">Fat</p>
          <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.fat}`}>
            {fmt1(nutritionTotals.fat_content_g)}g
          </p>
        </div>
        <div>
          <p className="text-subhead text-muted-foreground mb-1">Sugar</p>
          <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.sugar}`}>
            {fmt1(nutritionTotals.sugar_g)}g
          </p>
        </div>
        <div>
          <p className="text-subhead text-muted-foreground mb-1">Fiber</p>
          <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.fiber}`}>
            {fmt1(nutritionTotals.dietary_fiber_g)}g
          </p>
        </div>
        <div>
          <p className="text-subhead text-muted-foreground mb-1">Saturated Fat</p>
          <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.satFat}`}>
            {fmt1(nutritionTotals.saturated_fat_g)}g
          </p>
        </div>
        <div>
          <p className="text-subhead text-muted-foreground mb-1">Net Carbs</p>
          <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.netCarbs}`}>
            {fmt1(nutritionTotals.net_carbs_g)}g
          </p>
        </div>

        <div>
          <p className="text-subhead text-muted-foreground mb-1">GI</p>
          <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.glycemic}`}>
            {fmtInt(nutritionTotals.GI_estimate)}
          </p>
        </div>
        <div>
          <p className="text-subhead text-muted-foreground mb-1">GL</p>
          <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.glycemic}`}>
            {fmt1(nutritionTotals.GL_estimate)}
          </p>
        </div>
      </div>

      {bottomSlot ? (
        <div className="mt-4 border-t border-border/40 pt-4">{bottomSlot}</div>
      ) : null}
    </div>
  );
};

export default NutritionFactsCard;
